import type { Category, Todo } from "../../domain/entities.ts";

export interface WorkloadInsight {
  id: string;
  open: number;
  overdue: number;
  blocked: number;
  estimatedMinutes: number;
  unestimated: number;
}

export interface WipException {
  column: string;
  count: number;
  limit: number;
  taskIds: string[];
}

export interface CollectionInsights {
  open: number;
  completed: number;
  overdue: number;
  blocked: number;
  inactive: number;
  recentlyCompleted: number;
  estimatedMinutes: number;
  estimatedTaskIds: string[];
  inactiveTaskIds: string[];
  wipExceptions: WipException[];
  wipTaskIds: string[];
  workload: WorkloadInsight[];
}

export function collectionInsights(todos: Todo[], category: Category | undefined, visibleMemberIds: string[], today: string, now = Date.now(), dependencyTodos = todos): CollectionInsights {
  const active = todos.filter(todo => !todo.deleted && !todo.archivedAt);
  const open = active.filter(todo => !todo.completed);
  const completed = active.filter(todo => todo.completed);
  const taskById = new Map(dependencyTodos.filter(todo => !todo.deleted).map(todo => [todo.id, todo]));
  const isBlocked = (todo: Todo) => (todo.dependencyIds || []).some(id => { const dependency = taskById.get(id); return dependency && !dependency.completed && !dependency.deleted; });
  const inactiveTaskIds = open.filter(todo => todo.updatedAt <= now - 30 * 86_400_000).map(todo => todo.id);
  const estimated = open.filter(todo => (todo.estimatedMinutes || 0) > 0);
  const wipExceptions: WipException[] = [];
  if (category?.boardColumns?.length) {
    for (const [column, limit] of Object.entries(category.boardWipLimits || {})) {
      if (!limit) continue;
      const tasks = open.filter(todo => (todo.tagOrder || []).find(tag => todo.tags?.[tag]) === column);
      if (tasks.length > limit) wipExceptions.push({ column, count: tasks.length, limit, taskIds: tasks.map(todo => todo.id) });
    }
  }
  const workload = visibleMemberIds.map(id => {
    const assigned = open.filter(todo => (todo.assignedTo || []).includes(id));
    return { id, open: assigned.length, overdue: assigned.filter(todo => !!todo.dueDate && todo.dueDate < today).length, blocked: assigned.filter(isBlocked).length, estimatedMinutes: assigned.reduce((sum, todo) => sum + (todo.estimatedMinutes || 0), 0), unestimated: assigned.filter(todo => !todo.estimatedMinutes).length };
  }).filter(item => item.open > 0).sort((left, right) => right.open - left.open || right.estimatedMinutes - left.estimatedMinutes);
  const unassigned = open.filter(todo => !(todo.assignedTo || []).length);
  if (unassigned.length) workload.push({ id: "unassigned", open: unassigned.length, overdue: unassigned.filter(todo => !!todo.dueDate && todo.dueDate < today).length, blocked: unassigned.filter(isBlocked).length, estimatedMinutes: unassigned.reduce((sum, todo) => sum + (todo.estimatedMinutes || 0), 0), unestimated: unassigned.filter(todo => !todo.estimatedMinutes).length });
  return {
    open: open.length,
    completed: completed.length,
    overdue: open.filter(todo => !!todo.dueDate && todo.dueDate < today).length,
    blocked: open.filter(isBlocked).length,
    inactive: inactiveTaskIds.length,
    recentlyCompleted: completed.filter(todo => (todo.completedAt || todo.updatedAt) >= now - 7 * 86_400_000).length,
    estimatedMinutes: estimated.reduce((sum, todo) => sum + (todo.estimatedMinutes || 0), 0),
    estimatedTaskIds: estimated.map(todo => todo.id),
    inactiveTaskIds,
    wipExceptions,
    wipTaskIds: [...new Set(wipExceptions.flatMap(item => item.taskIds))],
    workload,
  };
}

export function insightDuration(minutes: number): string {
  if (!minutes) return "No estimates";
  const hours = Math.floor(minutes / 60); const remainder = minutes % 60;
  return hours ? `${hours}h${remainder ? ` ${remainder}m` : ""}` : `${remainder}m`;
}
